import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, CheckCircle2, XCircle, PalmtreeIcon, Clock3, Download, RefreshCw,
  UserCheck, BarChart3, TrendingUp, Sparkles, CalendarDays, Search,
} from "lucide-react";

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
function sessionForNow() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() < 12 * 60 + 30 ? "morning" : "afternoon";
}

const STATUS_META = {
  present:  { label: "Present",  color: "emerald", Icon: CheckCircle2 },
  absent:   { label: "Absent",   color: "rose",    Icon: XCircle },
  leave:    { label: "Leave",    color: "amber",   Icon: PalmtreeIcon },
  half_day: { label: "Half-day", color: "sky",     Icon: Clock3 },
};

function StatusChip({ status }) {
  if (!status) return <span className="text-xs text-slate-400">Not marked</span>;
  const m = STATUS_META[status];
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-${m.color}-100 dark:bg-${m.color}-950/40 text-${m.color}-700 dark:text-${m.color}-300`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

/* ---------------- Roster tab ---------------- */
function RosterTab({ readOnly }) {
  const [date, setDate] = useState(todayISO());
  const [session, setSession] = useState(sessionForNow());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teacher-attendance/roster", { params: { date, session } });
      setData(data);
      setDirty({});
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load roster");
    } finally { setLoading(false); }
  }, [date, session]);
  useEffect(() => { load(); }, [load]);

  const pick = (teacherId, status) => {
    if (readOnly) return;
    setDirty((p) => ({ ...p, [teacherId]: status }));
  };

  const effectiveStatus = (row) => dirty[row.teacher_id] ?? row.status;

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.rows;
    return data.rows.filter((r) => {
      const bag = [r.name, ...(r.subjects || []), ...(r.sections || []).map((s) => `${s.class_name}-${s.section}`)].join(" ").toLowerCase();
      return bag.includes(term);
    });
  }, [data, q]);

  const markAllPresent = () => {
    if (readOnly || !data?.rows) return;
    const next = { ...dirty };
    for (const r of data.rows) {
      if (!r.status) next[r.teacher_id] = "present";  // only fill blanks
    }
    setDirty(next);
    toast.success("Filled unmarked teachers as Present");
  };

  const save = async () => {
    const entries = Object.entries(dirty).map(([teacher_id, status]) => ({ teacher_id, status }));
    if (entries.length === 0) return toast.info("Nothing to save");
    setSaving(true);
    try {
      await api.post("/admin/teacher-attendance/mark", { date, session, entries });
      toast.success(`Saved ${entries.length} entr${entries.length === 1 ? "y" : "ies"}`);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const counters = data?.counts || {};
  const total = data?.total_teachers || 0;
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:flex sm:items-end sm:justify-between flex-wrap">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-40" data-testid="ta-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Session</label>
            <Select value={session} onValueChange={setSession}>
              <SelectTrigger className="h-10 w-36" data-testid="ta-session"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">Morning</SelectItem>
                <SelectItem value="afternoon">Afternoon</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-48">
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, subject, class…" className="h-10 pl-9" data-testid="ta-search" />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10" data-testid="ta-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {!readOnly && (
            <>
              <Button variant="outline" onClick={markAllPresent} className="h-10" data-testid="ta-fill-present">
                <UserCheck className="h-4 w-4 mr-1" /> Fill Unmarked → Present
              </Button>
              <Button onClick={save} disabled={saving || dirtyCount === 0} className="h-10 bg-emerald-600 hover:bg-emerald-700" data-testid="ta-save">
                {saving ? "Saving…" : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Non-working day banner */}
      {data?.non_working && (
        <div className="rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 px-4 py-2.5 text-sm flex items-center gap-2" data-testid="ta-nonworking-banner">
          <PalmtreeIcon className="h-4 w-4 shrink-0" />
          <span><b>{data.non_working}</b> — this is a non-working day (excluded from reports). You can still mark attendance if the school worked on this day.</span>
        </div>
      )}

      {/* Counters strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Teachers" value={total} tone="slate" Icon={Users} testid="stat-total" />
        <StatCard label="Present" value={counters.present || 0} tone="emerald" Icon={CheckCircle2} testid="stat-present" />
        <StatCard label="Absent" value={counters.absent || 0} tone="rose" Icon={XCircle} testid="stat-absent" />
        <StatCard label="Leave" value={counters.leave || 0} tone="amber" Icon={PalmtreeIcon} testid="stat-leave" />
        <StatCard label="Unmarked" value={counters.unmarked || 0} tone="sky" Icon={Clock3} testid="stat-unmarked" />
      </div>

      {/* Roster table */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ta-roster-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Teacher</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 hidden md:table-cell">Subjects</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 hidden lg:table-cell">Classes</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No teachers match.</td></tr>
              )}
              {!loading && rows.map((r) => {
                const st = effectiveStatus(r);
                const isDirty = r.teacher_id in dirty;
                const isAuto = r.source === "leave_auto";
                return (
                  <tr key={r.teacher_id} className={`border-t border-slate-100 dark:border-slate-800 ${isDirty ? "bg-sky-50/50 dark:bg-sky-950/20" : ""}`} data-testid={`ta-row-${r.teacher_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
                      {isAuto && <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Auto-marked from approved leave</div>}
                      {r.marked_by && !isAuto && <div className="text-[10px] text-slate-400 mt-0.5">by {r.marked_by}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-500 dark:text-slate-400 text-xs">
                      {r.subjects?.join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-500 dark:text-slate-400 text-xs">
                      {(r.sections || []).slice(0, 4).map((s) => `${s.class_name}-${s.section}`).join(", ") || "—"}
                      {(r.sections?.length || 0) > 4 && <span className="text-slate-400"> +{r.sections.length - 4}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {readOnly ? (
                        <div className="text-right"><StatusChip status={st} /></div>
                      ) : (
                        <div className="flex justify-end gap-1.5 flex-wrap">
                          {["present", "absent", "leave", "half_day"].map((s) => {
                            const meta = STATUS_META[s];
                            const active = st === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => pick(r.teacher_id, s)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                                  active
                                    ? `bg-${meta.color}-500 border-${meta.color}-500 text-white shadow-sm`
                                    : `bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-${meta.color}-400 hover:text-${meta.color}-600`
                                }`}
                                data-testid={`ta-btn-${r.teacher_id}-${s}`}
                              >
                                <meta.Icon className="h-3 w-3" /> {meta.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, Icon, testid }) {
  return (
    <div className={`rounded-2xl border border-${tone}-200/70 dark:border-${tone}-900/50 bg-${tone}-50/40 dark:bg-${tone}-950/20 px-4 py-3 flex items-center gap-3`} data-testid={testid}>
      <div className={`h-9 w-9 rounded-xl bg-${tone}-100 dark:bg-${tone}-900/40 flex items-center justify-center`}>
        <Icon className={`h-4 w-4 text-${tone}-600 dark:text-${tone}-300`} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
        <div className={`text-2xl font-bold text-${tone}-700 dark:text-${tone}-300`} style={{ fontFamily: "Outfit" }}>{value}</div>
      </div>
    </div>
  );
}

/* ---------------- Summary + Export tab ---------------- */
function monthBounds(m) {
  const [y, mo] = m.split("-").map(Number);
  const last = new Date(y, mo, 0).getDate();
  return [`${m}-01`, `${m}-${String(last).padStart(2, "0")}`];
}

function SummaryTab() {
  const [mode, setMode] = useState("month"); // month | range
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [from, setFrom] = useState(monthBounds(todayISO().slice(0, 7))[0]);
  const [to, setTo] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [downloading, setDownloading] = useState(false);

  const pickMonth = (m) => {
    if (!m) return;
    setMonth(m);
    const [f, t] = monthBounds(m);
    setFrom(f); setTo(t);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teacher-attendance/summary", { params: { from, to } });
      setRows(data.rows || []); setMeta({ days: data.days, working: data.working_days, nonWorking: data.non_working_days });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load summary");
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/teacher-attendance/export", {
        params: { from, to }, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `teacher_attendance_${from}_to_${to}.xlsx`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Export failed");
    } finally { setDownloading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:flex sm:items-end sm:justify-between flex-wrap">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Month / custom-range toggle */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Report period</label>
            <div className="flex items-center rounded-full bg-slate-100 dark:bg-slate-800 p-0.5 h-10" data-testid="ta-period-toggle">
              {[["month", "Month-wise"], ["range", "Custom range"]].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setMode(v)}
                  className={`px-3 h-9 rounded-full text-xs font-semibold transition-colors ${mode === v ? "bg-white dark:bg-slate-700 text-sky-600 dark:text-sky-300 shadow-sm" : "text-slate-500"}`}
                  data-testid={`ta-period-${v}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {mode === "month" ? (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Month</label>
              <Input type="month" value={month} onChange={(e) => pickMonth(e.target.value)} className="h-10 w-44" data-testid="ta-summary-month" />
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">From</label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-40" data-testid="ta-summary-from" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">To</label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-40" data-testid="ta-summary-to" />
              </div>
            </>
          )}
          <div className="flex items-end text-xs text-slate-500 pb-2" data-testid="ta-summary-meta">
            {meta.days ? `${meta.days} days · ${meta.working ?? "—"} working (excl. Sundays & holidays)` : ""}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10" data-testid="ta-summary-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          <Button onClick={download} disabled={downloading} className="h-10 bg-sky-600 hover:bg-sky-700" data-testid="ta-summary-download">
            <Download className="h-4 w-4 mr-1" /> {downloading ? "Preparing…" : "Download Excel"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ta-summary-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Teacher</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 hidden md:table-cell">Subjects</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">P</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">A</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">L</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">½</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-right">Attendance %</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No records yet in this range.</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.teacher_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ta-summary-row-${r.teacher_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{r.sections_count} class{r.sections_count === 1 ? "" : "es"}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-500">{r.subjects?.join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-center text-emerald-600 font-medium">{r.present}</td>
                  <td className="px-4 py-3 text-center text-rose-600 font-medium">{r.absent}</td>
                  <td className="px-4 py-3 text-center text-amber-600 font-medium">{r.leave}</td>
                  <td className="px-4 py-3 text-center text-sky-600 font-medium">{r.half_day}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      r.percentage >= 90 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" :
                      r.percentage >= 75 ? "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" :
                      "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                    }`}>{r.percentage}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Performance tab ---------------- */
function PerformanceTab() {
  const [from, setFrom] = useState(daysAgoISO(89));
  const [to, setTo] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teacher-performance", { params: { from, to } });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load performance");
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (tid) => {
    if (expanded === tid) { setExpanded(null); setDetail(null); return; }
    setExpanded(tid); setDetail(null); setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/teacher-performance/${tid}`, { params: { from, to } });
      setDetail(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed");
    } finally { setDetailLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:flex sm:items-end sm:justify-between flex-wrap">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 w-40" data-testid="ta-perf-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 w-40" data-testid="ta-perf-to" />
          </div>
        </div>
        <Button variant="outline" onClick={load} className="h-10" data-testid="ta-perf-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ta-perf-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300">Teacher</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 hidden md:table-cell">Handles</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">Attendance</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">Avg Marks</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">Pass Rate</th>
                <th className="px-4 py-3 font-semibold text-slate-600 dark:text-slate-300 text-center">Activity</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No teachers found.</td></tr>}
              {!loading && rows.map((r) => (
                <>
                  <tr key={r.teacher_id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/40 dark:hover:bg-slate-900/30 cursor-pointer" onClick={() => openDetail(r.teacher_id)} data-testid={`ta-perf-row-${r.teacher_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 dark:text-slate-100">{r.name}</div>
                      <div className="text-[10px] text-slate-400">{r.subjects?.join(", ") || "—"}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-500">
                      {(r.sections || []).slice(0, 3).map((s) => `${s.class_name}-${s.section}`).join(", ")}
                      {(r.sections?.length || 0) > 3 && <span className="text-slate-400"> +{r.sections.length - 3}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">{r.attendance_pct === null ? <span className="text-slate-400">—</span> : <PctPill v={r.attendance_pct} />}</td>
                    <td className="px-4 py-3 text-center">{r.avg_marks_pct === null ? <span className="text-slate-400">—</span> : <PctPill v={r.avg_marks_pct} />}</td>
                    <td className="px-4 py-3 text-center">{r.pass_rate_pct === null ? <span className="text-slate-400">—</span> : <PctPill v={r.pass_rate_pct} />}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-slate-300" title={`Tests: ${r.activity?.practice_tests} · HW: ${r.activity?.homework} · Guidance: ${r.activity?.guidance}`}>
                        <Sparkles className="h-3.5 w-3.5 text-amber-500" /> {r.activity?.total ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-sky-600">{expanded === r.teacher_id ? "Hide" : "Details"}</td>
                  </tr>
                  {expanded === r.teacher_id && (
                    <tr key={`${r.teacher_id}-detail`} className="bg-slate-50/70 dark:bg-slate-900/40">
                      <td colSpan={7} className="px-4 py-4" data-testid={`ta-perf-detail-${r.teacher_id}`}>
                        {detailLoading && <div className="text-slate-400 text-sm">Loading detail…</div>}
                        {detail && detail.teacher_id === r.teacher_id && <TeacherDetail t={detail} />}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PctPill({ v }) {
  const tone = v >= 75 ? "emerald" : v >= 50 ? "sky" : "rose";
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-${tone}-100 text-${tone}-700 dark:bg-${tone}-950/40 dark:text-${tone}-300`}>{v}%</span>;
}

function TeacherDetail({ t }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Subject performance (this range)</div>
        {(t.subject_performance || []).length === 0 ? (
          <div className="text-sm text-slate-400">No published marks yet.</div>
        ) : (
          <div className="space-y-1.5">
            {t.subject_performance.map((sp, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg bg-white dark:bg-slate-800/60 px-3 py-2 border border-slate-100 dark:border-slate-800">
                <div>
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{sp.subject}</div>
                  <div className="text-[10px] text-slate-400">{sp.class_name}-{sp.section} · {sp.records} marks</div>
                </div>
                <div className="flex items-center gap-2">
                  <PctPill v={sp.avg_pct} />
                  <span className="text-[10px] text-slate-400">pass {sp.pass_pct}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Test-by-test trend</div>
        {(t.exam_series || []).length === 0 ? (
          <div className="text-sm text-slate-400">Publish more exams to see trend comparison.</div>
        ) : (
          <div className="space-y-2">
            {t.exam_series.map((s, i) => (
              <div key={i} className="rounded-lg bg-white dark:bg-slate-800/60 px-3 py-2 border border-slate-100 dark:border-slate-800">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1.5">{s.subject} <span className="text-xs text-slate-400">· {s.class_name}-{s.section}</span></div>
                <div className="flex flex-wrap gap-1.5">
                  {s.buckets.map((b, j) => {
                    const prev = j > 0 ? s.buckets[j - 1].avg_pct : null;
                    const trend = prev == null ? null : b.avg_pct > prev ? "up" : b.avg_pct < prev ? "down" : "same";
                    return (
                      <span key={j} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        trend === "up" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" :
                        trend === "down" ? "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" :
                        "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      }`}>
                        <span className="text-slate-500 dark:text-slate-400">{b.exam_key}</span>
                        <span className="font-bold">{b.avg_pct}%</span>
                        {trend === "up" && <TrendingUp className="h-3 w-3" />}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <MiniStat label="Practice tests" value={t.activity?.practice_tests ?? 0} />
          <MiniStat label="Homework" value={t.activity?.homework ?? 0} />
          <MiniStat label="Guidance" value={t.activity?.guidance ?? 0} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800 px-3 py-2">
      <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
    </div>
  );
}

/* ---------------- Page shell ---------------- */
export default function AdminTeacherAttendance({ embedded = false }) {
  // Super admin is normally read-only across the app, but has been explicitly
  // granted staff-HR write rights on this screen so they can mark teacher
  // attendance when the primary admin is out.
  const [tab, setTab] = useState("roster");

  const body = (
    <>
      {!embedded && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Administration</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Staff attendance & performance</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Mark today&apos;s roster, analyse trends and download the complete report.</p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="ta-tabs">
          <TabsTrigger value="roster" data-testid="ta-tab-roster" className="gap-1.5"><CalendarDays className="h-4 w-4" /> Daily Roster</TabsTrigger>
          <TabsTrigger value="summary" data-testid="ta-tab-summary" className="gap-1.5"><BarChart3 className="h-4 w-4" /> Summary & Export</TabsTrigger>
          <TabsTrigger value="performance" data-testid="ta-tab-performance" className="gap-1.5"><Sparkles className="h-4 w-4" /> Performance</TabsTrigger>
        </TabsList>
        <TabsContent value="roster" className="mt-6"><RosterTab readOnly={false} /></TabsContent>
        <TabsContent value="summary" className="mt-6"><SummaryTab /></TabsContent>
        <TabsContent value="performance" className="mt-6"><PerformanceTab /></TabsContent>
      </Tabs>
    </>
  );

  return embedded ? body : <Layout>{body}</Layout>;
}
